import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import styled from "@emotion/styled";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";

import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

import { useNavigate, useLocation } from "react-router-dom";
import { addToCart, getCart } from "../../actions/cartAction";
import styles from "./ProductDetail.module.css"; // import CSS file
import Cart from "../Cart";
import Swatch from "./Swatch";
import "./swatches.css"; // import CSS file

import newItemMark from "../../images/newItemMark.svg";
import preOrderMark from "../../images/preorder.svg";
import { detailProduct } from "../../actions/productActions";
import Star from "../Helpers/icons/Star";
import InputCom from "../Helpers/InputCom";
import Selectbox from "../Helpers/Selectbox";
import ImageContainer from "./ImageContainer";
import PrimaryButton from "../Helpers/Buttons/PrimaryButton";
import SecondaryButton from "../Helpers/Buttons/SecondaryButton";
import SizeItem from "./SizeItem";
import ZoomThumbnail from "./ZoomThumbnail";

function decodeHTMLEntities(text) {
  const txt = document.createElement("textarea");
  txt.innerHTML = text;
  return txt.value;
}

// function to format date
function formatDate(dateStr) {
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const date = new Date(dateStr);
  const year = date.getFullYear().toString().slice(-2); // Get only the last two digits of the years
  const month = months[date.getMonth()];
  const day = date.getDate().toString().padStart(2, "0"); // Ensure the day is two digits
  return `${month}/${day}/${year}`; // Format: "Mon-Day-Year"
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Assuming mobile screens are <= 768px
    };

    handleResize(); // Call once to set initial value
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return isMobile;
};

export default function ProductView({
  collection,
  styleNumber,
  colorCode,
  fit,
  className,
  reportHandler,
}) {
  const containerStyle = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    border: "1px solid #D0D0D0",
  };

  const itemStyle = {
    border: "1px solid #D0D0D0",
    padding: "10px",
    flexBasis: "300px",
    marginRight: "10px",
    marginBottom: "10px",
  };

  const NoImage =
    "https://maevn-images.s3.us-east-2.amazonaws.com/swatches/NoImage.jpg";

  const NoImageDetail =
    "https://maevn-images.s3.us-east-2.amazonaws.com/580x630/NoImage.jpg";

  // get data from API
  const dispatch = useDispatch();

  // get productDetail
  const productDetails = useSelector((state) => state.productDetails);
  const { loading, error, product } = productDetails;

  // Maevn outlet's style numbers ====================================================================
  const styleNumbers = [
    1718,
    7318,
    1738,
    8728,
    8708,
    8308,
    7338,
    5308,
    1748,
    5208,
    7348, // EON
    1202,
    1102,
    9202,
    9102,
    9802,
    9302,
    1802,
    1302, // Blossom
    8101,
    2101,
    8102,
    2100,
    8100,
    2102, // Blossom signature
    1016,
    9626,
    1626,
    1026,
    1006,
    9016,
    9026,
    9006,
    8606, // Core
    8801,
    8802, // Smart
    9810, // Prints
    1767,
    9800,
  ];

  // New SS27 Arrival
  const SS27_COLORS = new Set([
    "PTWG",
    "HDCM",
    "BMBH",
    "ECRB",
    "FRHT",
    "PWPT",
    "PWFA",
    "FLPP",
    "WDCM",
    "SWEP",
  ]);

  // New FW26 Arrival
  const FW26_COLORS = new Set([
    "HLPW",
    "PAPW",
    "PTPL",
    "INBL",
    "SCGD",
    "PDRM",
    "WDPL",
    "PTSK",
    "SRBL",
  ]);

  // New SS26 Arrival
  const SS26_COLORS = new Set([
    "CBYL",
    "LVNT",
    "HTPW",
    "WDFP",
    "AMBW",
    "SPPW",
    "FLDZ",
    "TPBL",
    "PLPW",
  ]);

  const FW25_COLORS = new Set([
    "ATBL",
    "CUDD",
    "FROT",
    "FTSP",
    "GNZN",
    "LIEP",
    "RBOH",
    "RPAW",
    "SPSD",
    "TRAS",
  ]);

  // pre-filter for SS27 colors
  const matchesSS27Swatch = product?.colorSwatches?.some((c) =>
    SS27_COLORS.has(c.colorCode),
  );

  // pre-filter for FW26 colors
  const matchesFW26Swatch = product?.colorSwatches?.some((c) =>
    FW26_COLORS.has(c.colorCode),
  );

  // pre-filter for SS26 colors
  const matchesAnySwatch = product?.colorSwatches?.some((c) =>
    SS26_COLORS.has(c.colorCode),
  );
  const isSS27_1797 = product?.styleNumber === "1797" && matchesSS27Swatch;

  const isFW26_1797 = product?.styleNumber === "1797" && matchesFW26Swatch;

  const isSS26_1797 = product?.styleNumber === "1797" && matchesAnySwatch;

  const is1797 = product?.styleNumber === "1797";

  // pre-filter SS27 swatches
  const ss27Swatches = (
    (product?.styleNumber === "1797" ? product?.colorSwatches : []) ?? []
  ).filter((c) => c.isNew === 1 && SS27_COLORS.has(c.colorCode));

  // pre-filter FW26 swatches
  const fw26Swatches = (
    (product?.styleNumber === "1797" ? product?.colorSwatches : []) ?? []
  ).filter((c) => c.isNew === 1 && FW26_COLORS.has(c.colorCode));

  // pre-filter just once
  const ss26Swatches = (
    (product?.styleNumber === "1797" ? product?.colorSwatches : []) ?? []
  ).filter((c) => SS26_COLORS.has(c.colorCode));

  const fw25Swatches = ((is1797 ? product?.colorSwatches : []) ?? []).filter(
    (c) => FW25_COLORS.has(c.colorCode),
  );

  const previousSeasonSwatches = (product?.colorSwatches ?? []).filter((c) => {
    if (is1797) {
      return (
        !SS27_COLORS.has(c.colorCode) &&
        !FW26_COLORS.has(c.colorCode) &&
        !SS26_COLORS.has(c.colorCode) &&
        !FW25_COLORS.has(c.colorCode)
      );
    }

    return c.isNew === 0;
  });

  // show only isNew==1, and EXCLUDE SS26 codes when style is 1797
  const visibleSwatches = (product?.colorSwatches ?? []).filter(
    (c) =>
      c.isNew === 1 &&
      !(
        product?.styleNumber === "1797" &&
        (SS26_COLORS.has(c.colorCode) || FW26_COLORS.has(c.colorCode))
      ),
  );

  // Convert product?.styleNumber to number for comparison
  const styleNo = Number(product?.styleNumber);

  const isOutletStyle = styleNumbers.includes(styleNo);

  // convert special characters
  const decodedShortDescription = decodeHTMLEntities(product?.shortDescription);

  // long description
  const longDescriptionArray = product?.longDescription
    ? product.longDescription.split("*").filter((item) => item.trim())
    : [];

  // validate user
  const { search } = useLocation();
  const redirectInUrl = new URLSearchParams(search).get("redirect");
  const redirect = redirectInUrl || "/Login";

  const userSignin = useSelector((state) => state.userSignin);
  const { userInfo, userLoading, userError } = userSignin;
  const navigate = useNavigate();

  // permissions
  const wishListPermission = userInfo?.result.user.permissions.WishList;
  const shoppingCartPermission = userInfo?.result.user.permissions.ShoppingCart;
  const placeOrderPermission = userInfo?.result.user.permissions.PlaceOrder;

  // zoom window =====================================================================================
  // get images
  const [src, setSrc] = useState(NoImageDetail);
  const [zoomSrc, setZoomSrc] = useState(NoImageDetail);
  const [isZoomWindowOpen, setIsZoomWindowOpen] = useState(false);
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevDisabled, setPrevDisabled] = useState(false);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [zoomState, setZoomState] = useState(0);

  // Function to try loading an image at a given index in zoom window
  const tryLoadImage = (index, direction = 1) => {
    const img = new Image();
    img.src = product.imageDetailLinks[index].zoominURL;

    img.onload = () => {
      setSrc(product.imageDetailLinks[index].basicURL);
      setZoomSrc(product.imageDetailLinks[index].zoominURL);
      setCurrentIndex(index);
      setPrevDisabled(index === 0);
      setNextDisabled(index === product.imageDetailLinks.length - 1);
      return true;
    };

    img.onerror = () => {
      // If image fails to load, try the next one in the given direction
      let newIndex = (index + direction) % product.imageDetailLinks.length;
      if (newIndex < 0) newIndex = product.imageDetailLinks.length - 1; // Handle negative index
      if (newIndex !== index) {
        return tryLoadImage(newIndex, direction);
      }
      return false;
    };

    return true;
  };

  const imgElementRef = useRef(null);

  useEffect(() => {
    if (imgElementRef.current) {
      // Apply initial sizing logic on first render
      const imgElement = imgElementRef.current;
      imgElement.style.transform = `none`;
      imgElement.style.width = "auto";
      imgElement.style.height = "100%"; // Fit to container's height
      imgElement.style.maxWidth = "100%"; // Ensure it doesn't overflow horizontally
      imgElement.style.maxHeight = "100%"; // Ensure it doesn't overflow vertically
      imgElement.style.objectFit = "contain"; // Ensure the image is fully visible
    }
  }, []);

  // magnify the image in zoomin window
  const handleImageClick = (e) => {
    const imgElement = e.target;

    if (zoomState === 0) {
      // First click: fixed resolution 1740 x 1890 pixels
      imgElement.style.transform = `none`;
      imgElement.style.width = "1740px";
      imgElement.style.height = "1890px";
      imgElement.style.maxWidth = "none";
      imgElement.style.maxHeight = "none";

      setZoomState(1); // Move to the next zoom level
    } else if (zoomState === 1) {
      // Second click: fixed resolution 2175 x 2362.5 pixels
      imgElement.style.transform = `none`;
      imgElement.style.width = "2175px";
      imgElement.style.height = "2362.5px";
      imgElement.style.maxWidth = "none";
      imgElement.style.maxHeight = "none";

      setZoomState(2); // Move to the next zoom level
    } else {
      // Reset to initial state (third click)
      imgElement.style.transform = `none`;
      imgElement.style.width = "auto";
      imgElement.style.height = "100%"; // Fit to container's height
      imgElement.style.maxWidth = "100%";
      imgElement.style.maxHeight = "100%";
      imgElement.style.objectFit = "contain";

      setZoomState(0); // Reset zoom state
    }
  };

  const openZoomWindow = () => {
    setIsZoomWindowOpen(true);
  };

  const closeZoomWindow = () => {
    setZoomState(0);
    setIsZoomWindowOpen(false);
    setIsZoomedIn(false);
  };

  const toggleZoom = () => {
    setIsZoomedIn(!isZoomedIn);
  };

  // change thumbnails in zoom window
  const changeImgHandler = (current, zoom, index) => {
    setSrc(current);
    setZoomSrc(zoom);
    setCurrentIndex(index);
    setZoomState(0);

    const img = new Image();
    img.src = zoom;

    img.onload = () => {
      // Image loaded successfully, no further action needed
      setPrevDisabled(index === 0);
      setNextDisabled(index === product.imageDetailLinks.length - 1);
    };

    img.onerror = () => {
      const totalImages = product?.imageDetailLinks?.length || 0;
      let newIndex = index;

      if (totalImages > 1) {
        // Try loading the next image in the sequence
        do {
          newIndex = (newIndex + 1) % totalImages;
        } while (newIndex !== index && !tryLoadImage(newIndex));

        // Update the disabled states after attempting to load the next image
        setPrevDisabled(newIndex === 0);
        setNextDisabled(newIndex === product.imageDetailLinks.length - 1);
      } else {
        // If there's only one image or all fail, fall back to a default image
        setSrc(NoImage);
        setZoomSrc(NoImageDetail);
        setPrevDisabled(true);
        setNextDisabled(true);
      }
    };
  };

  // Zoom in responsive ===================================================================================
  const isMobile = useIsMobile();

  // product info =========================================================================================
  // swatches
  const [selectedColor, setSelectedColor] = useState(colorCode);
  const [selectedColorName, setSelectedColorName] = useState("");

  // fit
  const [selectedFit, setSelectedFit] = useState();

  // price
  const [unitPrice, setUnitPrice] = useState();
  const [discountedPrice, setDiscountedPrice] = useState();
  const [isFirstRender, setIsFirstRender] = useState(true); // to display price range when user's first access

  // ETA tooltip
  const [showTooltip, setShowTooltip] = useState(false);

  // size
  const [selectedItemIdx, setSelectedItemIdx] = useState(0); // selectedItem==index
  const [itemSize, setItemSize] = useState("");

  // quantity
  const [quantity, setQuantity] = useState(1);

  // sales marks
  const [isPreorder, setIsPreorder] = useState(false);
  const [discountRate, setDiscountRate] = useState(0);
  const [isNew, setIsNew] = useState(false);
  const [isNewStyle, setIsNewStyle] = useState(false);

  // quantity
  const [quantities, setQuantities] = React.useState([]);

  // alert for input box
  const [inputValue, setInputValue] = useState("");

  // dialog alert for backorder
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  // move to shopping cart
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userId, setUserId] = useState(userInfo.result.user.userId);

  // set selectedColor
  const handleSwatchClick = (color) => {
    setIsFirstRender(false); // switch to unitPrice display

    setQuantities([]); // Reset the quantities when color is changed

    setSelectedColor(color);

    // Momentum Mens
    if (
      styleNumber === "5801" ||
      styleNumber === "5802" ||
      styleNumber === "5861" ||
      styleNumber === "5891" ||
      styleNumber === "5892"
    ) {
      navigate(`/Momentum-Mens/${styleNumber}/${color}/${selectedFit}`);
    } else {
      navigate(`/Scrubs/${collection}/${styleNumber}/${color}/${selectedFit}`);
    }
  };

  // set Fit
  const handleFitClick = (fit) => {
    setIsFirstRender(false); // switch to unitPrice display
    setQuantities([]); // Reset the quantities when fit is changed
    setSelectedFit(fit);

    // Momentum Mens
    if (
      styleNumber === "5801" ||
      styleNumber === "5802" ||
      styleNumber === "5861" ||
      styleNumber === "5891" ||
      styleNumber === "5892"
    ) {
      navigate(`/Momentum-Mens/${styleNumber}/${selectedColor}/${fit}`);
    } else {
      navigate(`/${collection}/${styleNumber}/${selectedColor}/${fit}`);
    }
  };

  // set index for selectedItem
  const handleSizeClick = (index) => {
    setIsFirstRender(false);
    setSelectedItemIdx(index);

    const item = product.sizes[index];

    const currentUnitPrice = Number(item.unitPrice || 0);
    const currentDiscountRate = Number(item.discountRate || 0);

    setUnitPrice(currentUnitPrice);
    setDiscountRate(currentDiscountRate);

    if (currentDiscountRate > 0) {
      const discountedAmount = (currentUnitPrice * currentDiscountRate) / 100;
      const roundedDiscountAmount = Math.round(discountedAmount * 100) / 100;
      setDiscountedPrice(currentUnitPrice - roundedDiscountAmount);
    } else {
      setDiscountedPrice(currentUnitPrice);
    }
  };

  // set checking entered quantity
  const handleQuantityChange = (
    e,
    pid,
    availableQuantity,
    isPreorderQty,
    discountRateQty,
  ) => {
    const inputQuantity = parseInt(e.target.value, 10);

    if (Number.isNaN(inputQuantity) || inputQuantity < 0) {
      setQuantities((prevQuantities) =>
        prevQuantities.filter((item) => item.pid !== pid),
      );
      return;
    }

    if (inputQuantity === 0) {
      setDialogMessage("The minimum order quantity is 1 or more.");
      setDialogOpen(true);
    } else {
      if (inputQuantity > availableQuantity) {
        setDialogMessage(
          "You can place backorders for any remaining quantities that exceed the current orderable quantity. If you prefer not to place a backorder, kindly adjust the quantity of the item in your order to match the currently available stock.",
        );
        setDialogOpen(true);
      }
      setQuantities((prevQuantities) => {
        const updatedQuantities = prevQuantities.filter(
          (item) => item.pid !== pid,
        );
        updatedQuantities.push({
          pid,
          quantity: inputQuantity,
          isPreorderQty,
          discountRateQty,
        });
        return updatedQuantities;
      });
    }
  };

  // Styled Dialog Content
  const StyledDialogContent = styled(DialogContent)``;

  // Styled Dialog Actions
  const StyledDialogActions = styled(DialogActions)`
    display: flex;
    justify-content: space-evenly;
    padding: 15px;
  `;

  // Styled Dialog with Paper Props
  const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
      display: inline-flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      padding: 30px;
      border-radius: 10px;
      background: var(--Base-White, #fff);
    }
  `;

  // go to checkout
  const handleCheckOutClick = async (e) => {
    e.preventDefault();

    // Check if all quantities are null
    if (
      quantities.length === 0 ||
      quantities.every((item) => item.quantity === null)
    ) {
      setDialogMessage("Please enter the quantity you would like to order.");
      setDialogOpen(true);
    } else {
      dispatch(addToCart(quantities));

      navigate(`/cart`);
    }
  };

  // move to cart or stay here
  const handleStayHereClick = () => {
    setIsModalOpen(false);
  };

  const handleGoToCartClick = () => {
    navigate(`/cart`);
    setIsModalOpen(false);
  };

  // add item to cart
  const handleAddToCartClick = async (e) => {
    e.preventDefault();

    // Check if all quantities are null
    if (
      quantities.length === 0 ||
      quantities.every((item) => item.quantity === null)
    ) {
      setDialogMessage("Please enter the quantity you would like to order.");
      setDialogOpen(true);
    } else {
      try {
        await dispatch(addToCart(quantities));
        await dispatch(getCart(userId)); // Update qty of cart in header
        setIsModalOpen(true);
        setQuantities([]); // Clear quantities
      } catch (error) {
        console.error("Failed to add item to cart:", error);
      }
    }
  };

  // set badges
  useEffect(() => {
    const colorName = product?.currentColorName;
    setSelectedColorName(colorName);

    if (
      product?.sizes &&
      Array.isArray(product?.sizes) &&
      selectedItemIdx !== null
    ) {
      const item = product.sizes[selectedItemIdx];

      // Set Preorder
      const texasTime = new Date().toLocaleString("en-US", {
        timeZone: "America/Chicago",
      });

      // Set Preorder if item.eta is in the future
      if (
        item.qtyAvailable === 0 &&
        (item.nextAvailDate || new Date(item.eta) > new Date(texasTime))
      ) {
        setIsPreorder(true);
      } else {
        setIsPreorder(false);
      }

      // Check if selectedColor matches any colorSwatch with isNew === 1
      const hasNewColor =
        product.colorSwatches?.some(
          (swatch) => swatch.colorCode === selectedColor && swatch.isNew === 1,
        ) || false;
      setIsNew(hasNewColor);

      const hasNewStyle =
        product.colorSwatches?.every((swatch) => swatch.isNew === 1) || false;
      setIsNewStyle(hasNewStyle);

      // Set discount rate and price calculation
      const currentUnitPrice = Number(item.unitPrice || 0);
      const currentDiscountRate = Number(item.discountRate || 0);

      setUnitPrice(currentUnitPrice);
      setDiscountRate(currentDiscountRate);

      if (currentDiscountRate > 0) {
        const discountedAmount = (currentUnitPrice * currentDiscountRate) / 100;
        const roundedDiscountAmount = Math.round(discountedAmount * 100) / 100;
        setDiscountedPrice(currentUnitPrice - roundedDiscountAmount);
      } else {
        setDiscountedPrice(currentUnitPrice);
      }

      setItemSize(item?.itemSize || "");
    }
  }, [product, selectedColor, selectedItemIdx]);

  // set the default basic & zoom image
  useEffect(() => {
    if (product && product?.imageDetailLinks) {
      setSrc(product?.imageDetailLinks[0].basicURL);
      setZoomSrc(product?.imageDetailLinks[0].zoominURL);
      setCurrentIndex(0); // Reset the index when the product changes
    }
  }, [product]);

  // default fit
  useEffect(() => {
    if (product && product.fitList) {
      setSelectedFit(product?.currentFit); // if click fit button, it will call API and then re-render productDetail component
    }
  }, [product]);

  // call APIs for user authentication and detailProduct
  useEffect(() => {
    if (!userInfo) {
      alert("Please sign in to access our website. ");

      // Remove IRG token
      localStorage.removeItem("IRGAccesstoken");
      localStorage.removeItem("IRGtokenExpiry");

      navigate(redirect);
    } else {
      console.log("You are already logined ");
    }
    dispatch(
      detailProduct({
        stylenumber: styleNumber,
        colorcode: colorCode,
        fit,
      }),
    );
  }, [dispatch, navigate, redirect, userInfo, styleNumber, colorCode, fit]);

  return (
    <div
      className={`product-view w-full lg:flex justify-between ${
        className || ""
      }`}
    >
      <div data-aos="fade-right" className="lg:w-1/2 xl:mr-[70px] lg:mr-[50px]">
        <div className="w-full">
          <div className="w-full border border-white flex justify-center items-center overflow-hidden relative mb-3">
            {/* Image container - Inner Zoom */}
            <div
              className="image-container relative"
              onClick={() => !isMobile && openZoomWindow()}
            >
              <img
                src={`${src}`}
                alt=""
                style={{ display: "none" }} // Hide the fallback img tag
                onError={() => {
                  setSrc(NoImageDetail);
                  setZoomSrc(NoImageDetail);
                }}
              />
              <InnerImageZoom
                src={`${src}`}
                zoomSrc={`${zoomSrc}`}
                zoomType="hover"
                zoomPreload
                onMouseEnter={() => setZoomSrc(zoomSrc)}
                onMouseLeave={() => setZoomSrc(NoImageDetail)}
              />
            </div>
            {/* Badges */}
            {(() => {
              const badges = [];

              // If hasNewStyle is true, show "NEW STYLE", otherwise show "NEW COLOR"
              if (isNewStyle) {
                badges.push(
                  <div
                    key="new-style"
                    className="newStyleProductdetail-badge"
                    style={{ marginRight: "8px" }}
                  >
                    NEW STYLE
                  </div>,
                );
              } else if (isNew) {
                badges.push(
                  <div
                    key="new-color"
                    className="newColorProductdetail-badge"
                    style={{ marginRight: "8px" }}
                  >
                    NEW COLOR
                  </div>,
                );
              }

              if (isPreorder) {
                badges.push(
                  <div
                    key="is-preorder"
                    className="preOrderProductdetail-badge"
                  >
                    PREORDER
                  </div>,
                );
              }

              return badges.length > 0 ? (
                <div className="absolute left-[20px] top-[20px] flex gap-1">
                  {badges}
                </div>
              ) : null;
            })()}

            {discountRate > 0 && (
              <div className="w-[90px] h-[90px] rounded-full bg-qyellow text-qblack flex justify-center items-center text-xl font-medium absolute left-[450px] top-[30px]">
                <span>{Math.abs(discountRate)}%</span>
              </div>
            )}
          </div>

          {/*   Thumbnails Image container (no zoom) */}
          <div className="flex flex-wrap" style={{ gap: "8px" }}>
            {product?.imageDetailLinks &&
              product?.imageDetailLinks.length > 0 &&
              product?.imageDetailLinks.map((img, index) => (
                <ZoomThumbnail
                  key={img.id}
                  img={img}
                  src={src}
                  changeImgHandler={(current, zoom) =>
                    changeImgHandler(current, zoom, index)
                  }
                  NoImage={NoImage}
                />
              ))}
          </div>
        </div>
      </div>
      {/* Zoom Window popup */}
      {!isMobile && isZoomWindowOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-40">
          <div className="relative w-full h-full p-4 bg-white rounded-lg flex flex-col">
            <span
              className="absolute top-2 right-3 text-gray-500 focus:outline-none text-4xl py-2 px-6 rounded-full cursor-pointer z-50"
              onClick={closeZoomWindow}
              style={{ zIndex: 1000 }}
            >
              &times;
            </span>

            {/* Zoom Window Image container */}
            <div
              className="zoom-modal-container flex justify-center items-center mx-16 "
              style={{
                width: "auto",
                height:
                  zoomState === 0
                    ? "100%"
                    : `${imgElementRef.current?.naturalHeight}px`,
                overflow: zoomState === 0 ? "auto" : "scroll", // Enable scroll when zoomed in
                cursor: zoomState === 2 ? "zoom-out" : "zoom-in",
                display: "flex", // Center the image within the container
                justifyContent: "center", // Horizontal centering
                alignItems: zoomState === 0 ? "center" : "start", // Align to start when zoomed in
                position: "relative", // Ensure it fits within the modal
              }}
            >
              <img
                src={zoomSrc}
                alt="Product"
                ref={imgElementRef}
                style={{
                  transition:
                    "transform 0.1s ease-in-out, transform-origin 0.1s ease-in-out",
                  transformOrigin: zoomState === 0 ? "top center" : "top", // Align transform origin based on zoomState
                  objectFit: zoomState === 0 ? "contain" : "unset", // Use 'unset' when zoomed in to show full resolution
                  height:
                    zoomState === 0
                      ? "100%"
                      : `${imgElementRef.current?.naturalHeight}px`, // Adjust height based on zoomState
                  width:
                    zoomState === 0
                      ? "auto"
                      : `${imgElementRef.current?.naturalWidth}px`, // Adjust width based on zoomState
                }}
                onClick={handleImageClick}
              />
            </div>

            {/*  Thumbnails container in Zoom Window  */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/*  Previous Button in Zoom Window */}
              <button
                type="button"
                className="zoom-image-nav-button left"
                onClick={() => {
                  setZoomState(0);
                  let prevIndex = currentIndex - 1;
                  if (prevIndex < 0)
                    prevIndex = product.imageDetailLinks.length - 1; // Cycle to the first image
                  tryLoadImage(prevIndex, -1); // -1 indicates moving backwards
                }}
                style={{
                  opacity: 1,
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "50%", // Ensure the button itself is also round if needed
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="#808080"
                  style={{
                    borderRadius: "50%", // Ensure the SVG is round
                    transition: "fill 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!nextDisabled) e.currentTarget.style.fill = "#696969"; // Very light grey on hover
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.fill = "#808080"; // Original grey color
                  }}
                >
                  <circle cx="12" cy="12" r="10" fill="none" />
                  <path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 12l4.5-4.5-1.527-1.5-5.973 6 5.973 6 1.527-1.5-4.5-4.5z" />
                </svg>
              </button>

              {/* Thumbnails List in Zoom Window  */}
              <div
                className="mt-4 flex justify-center space-x-2 "
                style={{
                  marginRight: "25px",
                  marginLeft: "25px",
                }}
              >
                {product?.imageDetailLinks?.map((img, index) => (
                  <ZoomThumbnail
                    key={img.id}
                    img={img}
                    src={src}
                    changeImgHandler={(current, zoom) =>
                      changeImgHandler(current, zoom, index)
                    }
                    NoImage={NoImage}
                  />
                ))}
              </div>

              {/* Next Button */}
              <button
                type="button"
                className="zoom-image-nav-button right"
                onClick={() => {
                  setZoomState(0);
                  let nextIndex = currentIndex + 1;
                  if (nextIndex >= product.imageDetailLinks.length)
                    nextIndex = 0;
                  tryLoadImage(nextIndex, 1); // 1 indicates moving forwards
                }}
                disabled={nextDisabled} // Disable if the current index is the last image
                style={{
                  opacity: 1,
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "50%", // Ensure the button itself is also round if needed
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="#808080"
                  style={{
                    borderRadius: "50%", // Ensure the SVG is round
                    transition: "fill 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!nextDisabled) e.currentTarget.style.fill = "#696969"; // Very light grey on hover
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.fill = "#808080"; // Original grey color
                  }}
                >
                  <circle cx="12" cy="12" r="10" fill="none" />
                  <path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm2 12l-4.5 4.5 1.527 1.5 5.973-6-5.973-6-1.527 1.5 4.5 4.5z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}{" "}
      {/* End for zooom window code  */}
      <div className="flex-1">
        <div className="product-details w-full mt-10 lg:mt-0">
          {/* Style No. */}
          <span
            data-aos="fade-up"
            className="text-qgray text-xs font-medium uppercase tracking-wider mb-2 inline-block"
          >
            Style no. {product?.styleNumber}
          </span>
          {/* Description */}
          <p data-aos="fade-up" className="text-xl font-bold text-qblack mb-4">
            {decodedShortDescription}
          </p>

          {/* Price */}
          <div data-aos="fade-up" className="flex space-x-2 items-center mb-7">
            {isFirstRender ? (
              <span
                className="text-2xl font-500 text-qred"
                style={{
                  color: "var(--Body-txt-Grey, #474747)",
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  fontStyle: "normal",
                  fontWeight: "700",
                  lineHeight: "normal",
                }}
              >
                {/* ${product?.priceMin?.toFixed(2)} - $
                {product?.priceMax?.toFixed(2)} */}
                ${product?.priceMin?.toFixed(2)}
              </span>
            ) : discountRate > 0 ? (
              <>
                <span
                  className="text-xl font-500 text-qgray line-through"
                  style={{
                    color: "var(--Body-txt-Grey, #474747)",
                    fontFamily: "Roboto",
                    fontSize: "20px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "normal",
                  }}
                >
                  ${unitPrice.toFixed(2)}
                </span>
                <span
                  className="text-2xl font-500 text-qred"
                  style={{
                    color: "var(--Body-txt-Grey, #474747)",
                    fontFamily: "Roboto",
                    fontSize: "20px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "normal",
                  }}
                >
                  ${discountedPrice.toFixed(2)}
                </span>
              </>
            ) : (
              <span
                className="text-2xl font-500 text-qred"
                style={{
                  color: "var(--Body-txt-Grey, #474747)",
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  fontStyle: "normal",
                  fontWeight: "700",
                  lineHeight: "normal",
                }}
              >
                ${unitPrice}
              </span>
            )}
          </div>

          {/* Long description */}
          <ul
            data-aos="fade-up"
            className="text-qgray text-sm text-normal mb-[30px] leading-7"
          >
            {longDescriptionArray.map((item) => (
              <li key={item.trim()}>* {item.trim()}</li>
            ))}
          </ul>

          {/* Size */}
          <div data-aos="fade-up" className="colors mb-[30px]">
            <div>
              {product?.dimension && (
                <span className="text-sm font-normal uppercase text-qgray inline-block">
                  Size
                </span>
              )}
            </div>
            <div>
              {product?.dimension && (
                <span className="text-sm font-normal text-qgray  inline-block">
                  *&nbsp;{product?.dimension}
                </span>
              )}
            </div>
          </div>

          {/* Fabric contents */}
          <div data-aos="fade-up" className="colors mb-[30px]">
            <div>
              <span className="text-sm font-bold uppercase  text-qgray inline-block">
                Fabric Content
              </span>
            </div>
            <div>
              <span className="text-sm font-normal text-qgray  inline-block">
                *&nbsp;{product?.fabricContent}
              </span>
            </div>
          </div>

          {/* Curent Color Name */}
          <div data-aos="fade-up" className="colors mb-[10px]">
            <div>
              <span className="font-bold text-sm uppercase text-qblack inline-block">
                COLOR:&nbsp;
              </span>
              <span className="	 text-sm text-qgray inline-block">
                {selectedColorName}&nbsp;
              </span>
              <span className="font-semibold text-sm text-qgray inline-block">
                ({selectedColor})
              </span>
            </div>
          </div>

          {/*  Swatch containers - New SS27 Arrival */}
          {ss27Swatches.length > 0 && (
            <div data-aos="fade-up" className="colors">
              <span className="font-semibold text-[13px] text-qgray inline-block mb-[2px]">
                NEW SS27 Arrival
              </span>
            </div>
          )}
          {ss27Swatches.length > 0 && (
            <div data-aos="fade-up" className="flex flex-wrap">
              {ss27Swatches.map((color) => (
                <div
                  key={color.colorCode}
                  className="mr-2 mb-1 flex flex-col items-center"
                >
                  <img
                    src={`${color.colordotURL}`}
                    alt={color.colorName}
                    onClick={() => handleSwatchClick(color.colorCode)}
                    className={`w-[40px] h-[40px] border border-qgray-border cursor-pointer object-contain  ${
                      selectedColor === color.colorCode
                        ? "border-2 border-[#04ADD1]"
                        : "border border-qgray-border"
                    } ${src !== color.colordotURL ? "opacity-100" : ""} `}
                    onError={(event) => {
                      event.target.onerror = null; // prevent infinite error loop
                      event.target.src = `${NoImage}`; // set fallback image
                    }}
                  />

                  <span className="text-xs text-qgray ">{color.colorCode}</span>
                </div>
              ))}
            </div>
          )}

          {/* Swatch containers - FW26 Arrival */}
          {fw26Swatches.length > 0 && (
            <div data-aos="fade-up" className="colors">
              <span className="font-semibold text-[13px] text-qgray inline-block mb-[2px]">
                FW26 Arrival
              </span>
            </div>
          )}
          {fw26Swatches.length > 0 && (
            <div data-aos="fade-up" className="flex flex-wrap">
              {fw26Swatches.map((color) => (
                <div
                  key={color.colorCode}
                  className="mr-2 mb-1 flex flex-col items-center"
                >
                  <img
                    src={`${color.colordotURL}`}
                    alt={color.colorName}
                    onClick={() => handleSwatchClick(color.colorCode)}
                    className={`w-[40px] h-[40px] border border-qgray-border cursor-pointer object-contain  ${
                      selectedColor === color.colorCode
                        ? "border-2 border-[#04ADD1]"
                        : "border border-qgray-border"
                    } ${src !== color.colordotURL ? "opacity-100" : ""} `}
                    onError={(event) => {
                      event.target.onerror = null; // prevent infinite error loop
                      event.target.src = `${NoImage}`; // set fallback image
                    }}
                  />

                  <span className="text-xs text-qgray ">{color.colorCode}</span>
                </div>
              ))}
            </div>
          )}

          {/* Swatch containers - SS26 Arrival */}
          {ss26Swatches.length > 0 && (
            <div data-aos="fade-up" className="colors">
              <span className="font-semibold text-[13px] text-qgray inline-block mb-[2px]">
                SS26 Arrival
              </span>
            </div>
          )}

          {ss26Swatches.length > 0 && (
            <div data-aos="fade-up" className="flex flex-wrap">
              {ss26Swatches.map((color) => (
                <div
                  key={color.colorCode}
                  className="mr-2 mb-1 flex flex-col items-center"
                >
                  <img
                    src={color.colordotURL}
                    alt={color.colorName}
                    onClick={() => handleSwatchClick(color.colorCode)}
                    className={`w-[40px] h-[40px] cursor-pointer object-contain ${
                      selectedColor === color.colorCode
                        ? "border-2 border-[#04ADD1]"
                        : "border border-qgray-border"
                    }`}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = NoImage;
                    }}
                  />

                  <span className="text-xs text-qgray">{color.colorCode}</span>
                </div>
              ))}
            </div>
          )}

          {/*  Swatch containers -  FW25 Arrival */}
          {fw25Swatches.length > 0 && (
            <div data-aos="fade-up" className="colors mb-[15px]">
              <span className="font-semibold text-[13px] text-qgray inline-block mb-[2px]">
                FW25 Arrival
              </span>

              <div className="flex flex-wrap">
                {fw25Swatches.map((color) => (
                  <div
                    key={color.colorCode}
                    className="mr-2 mb-1 flex flex-col items-center"
                  >
                    <img
                      src={`${color.colordotURL}`}
                      alt={color.colorName}
                      onClick={() => handleSwatchClick(color.colorCode)}
                      className={`w-[40px] h-[40px] border border-qgray-border cursor-pointer object-contain ${
                        selectedColor === color.colorCode
                          ? "border-2 border-[#04ADD1]"
                          : "border border-qgray-border"
                      } ${src !== color.colordotURL ? "opacity-100" : ""} `}
                      onError={(event) => {
                        event.target.onerror = null;
                        event.target.src = `${NoImage}`;
                      }}
                    />
                    <span className="text-xs text-qgray ">
                      {color.colorCode}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Swatch containers - Previous Seasons (original) */}
          {product?.colorSwatches &&
            product.colorSwatches.length > 0 &&
            product.colorSwatches.some((color) => color.isNew === 0) && (
              <div data-aos="fade-up" className="colors mb-[15px]">
                <div className="flex flex-wrap">
                  {previousSeasonSwatches.map((color) => (
                    <div
                      key={color.colorCode}
                      className="mr-2 mb-1 flex flex-col items-center"
                    >
                      <img
                        src={`${color.colordotURL}`}
                        alt={color.colorName}
                        onClick={() => handleSwatchClick(color.colorCode)}
                        className={`w-[40px] h-[40px] border border-qgray-border cursor-pointer object-contain  ${
                          selectedColor === color.colorCode
                            ? "border-2 border-[#04ADD1]"
                            : "border border-qgray-border"
                        } ${src !== color.colordotURL ? "opacity-100" : ""} `}
                        onError={(event) => {
                          event.target.onerror = null; // prevent infinite error loop
                          event.target.src = `${NoImage}`; // set fallback image
                        }}
                      />
                      <span className="text-xs text-qgray ">
                        {color.colorCode}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Fit containers */}
          <div data-aos="fade-up" className="colors mb-[15px]">
            <span className="text-sm font-bold text-qblack mb-[5px] inline-block">
              FIT:&nbsp;
            </span>
            <span className="text-sm text-qgray mb-[5px] inline-block">
              {selectedFit === "Reg" ? "Regular" : selectedFit}
            </span>
            <div data-aos="fade-up" className="mb-[30px]">
              {product?.fitList &&
                product.fitList.filter((val) => val !== null && val !== "")
                  .length > 0 && (
                  <div className={styles.fitButtonsContainer}>
                    {product.fitList
                      .filter((val) => val !== null && val !== "")
                      .map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => handleFitClick(val)}
                          className={`${styles.fitButton} ${
                            selectedFit === val ? styles.selected : ""
                          }`}
                        >
                          {val}
                        </button>
                      ))}
                  </div>
                )}
            </div>
          </div>

          {/* Size containers */}
          <div data-aos="fade-up" className="product-size mb-[30px]">
            <div className="justify-between items-center ">
              {/* Size Label */}
              <span className="text-sm font-bold uppercase text-qblack mb-[5px] inline-block">
                SIZE:&nbsp;
              </span>
              <span className="text-sm uppercase text-qgray mb-[5px] inline-block">
                {itemSize}
              </span>
            </div>

            {/* size container */}
            <div className="w-full" style={{ margin: "-5.5px" }}>
              <div
                className="size-container "
                style={{
                  background: "var(--Base-White, #FFF)",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {/* product sizes */}
                  {product?.sizes &&
                  Array.isArray(product?.sizes) &&
                  product?.sizes.length > 0 ? (
                    product?.sizes
                      .filter((item, index) => {
                        // Exclude items where b2bActiveFlag is false
                        if (!item.b2bActiveFlag) {
                          return false; // Exclude this item
                        }
                        // Size container rule : Show every sizes
                        return true; // Include this item
                      })

                      .map((item, index) => {
                        //  ETA Condition : if qtyAvailable <= 0, nextAvailDate is null, and it's between the first and last item
                        const isInactiveInput =
                          item.erpActiveFlag === 90 ||
                          (item.qtyAvailable <= 0 &&
                            (!item?.nextAvailDate ||
                              item?.nextAvailDate.trim() === ""));
                        // index !== 0 &&

                        return (
                          <SizeItem
                            key={item.pid}
                            item={item}
                            index={index}
                            handleSizeClick={handleSizeClick}
                            handleQuantityChange={handleQuantityChange}
                            quantities={quantities}
                            selectedItemIdx={selectedItemIdx}
                            isOutletStyle={isOutletStyle} // For MaevnOutlet product logic
                            isInactiveInput={isInactiveInput} // Pass down inactive input condition
                          />
                        );
                      })
                  ) : product?.sizes === undefined ? null : (
                    <div>No sizes available</div>
                  )}
                </div>
              </div>

              {/* Backorder modal */}
              <StyledDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="xs" // Limit the width of the dialog
                fullWidth
              >
                <StyledDialogContent>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V9H11V15ZM11 7H9V5H11V7Z"
                        fill="var(--Warning-Color---Red, #F44)"
                      />
                    </svg>
                    <div
                      style={{
                        width: "225px",
                        color: "var(--Base-Black, #000)",
                        fontFamily: "Roboto",
                        fontSize: "24px",
                        fontWeight: "500",
                      }}
                    >
                      Check before you go
                    </div>
                  </div>
                  <div
                    style={{
                      width: "325px",
                      marginTop: "15px",
                      color: "var(--Body-txt-Grey, #474747)",
                      fontFamily: "Lato",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: "400",
                      lineHeight: "22px",
                    }}
                  >
                    {dialogMessage}
                  </div>
                </StyledDialogContent>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <PrimaryButton
                    width="121px"
                    height="40px"
                    fontSize="16px"
                    onClick={() => setDialogOpen(false)}
                  >
                    Confirm
                  </PrimaryButton>
                </div>
              </StyledDialog>
            </div>
          </div>

          <div
            data-aos="fade-up"
            className="quantity-card-wrapper w-full flex items-center h-[50px] space-x-[10px] mb-[30px]"
          >
            <div className="flex-1 h-full">
              <div className="flex w-full h-full space-x-2">
                {shoppingCartPermission === 1 && (
                  <PrimaryButton
                    width="218px"
                    height="50px"
                    fontSize="18px"
                    onClick={handleAddToCartClick}
                  >
                    Add To Cart
                  </PrimaryButton>
                )}
                <Dialog
                  open={isModalOpen}
                  onClose={handleStayHereClick}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                  maxWidth="xs" // Limit the width of the dialog
                  fullWidth
                >
                  <StyledDialogContent
                    style={{ paddingTop: "0", paddingBottom: "0" }}
                  >
                    <DialogContentText
                      id="alert-dialog-description"
                      style={{ textAlign: "center", lineHeight: "80px" }}
                    >
                      A new item has been added to your shopping Cart.
                    </DialogContentText>
                  </StyledDialogContent>

                  <StyledDialogActions
                    style={{ marginTop: "0", justifyContent: "flex-end" }}
                  >
                    <SecondaryButton
                      width="180px"
                      height="40px"
                      fontSize="16px"
                      onClick={handleStayHereClick}
                      // disabled={disabled}
                    >
                      Keep Shopping
                    </SecondaryButton>

                    <PrimaryButton
                      width="180px"
                      height="40px"
                      fontSize="16px"
                      onClick={handleGoToCartClick}
                      // disabled={disabled}
                    >
                      Go To Cart
                    </PrimaryButton>
                  </StyledDialogActions>
                </Dialog>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
